import type { Product } from '../data/products'
import type { CaseStudy } from '../data/caseStudies'
import type { BlogPost } from '../data/blog'

export const SITE_URL = ((import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://polakis.co.ke').replace(/\/$/, '')
export const SITE_NAME = 'Polakis Technologies'
export const DEFAULT_TITLE = 'Polakis Technologies | Enterprise Software Products'
export const DEFAULT_DESCRIPTION =
  'Polakis Technologies builds enterprise software products for healthcare, insurance, fleet, logistics, property and asset-intensive organizations across Africa and beyond.'
export const DEFAULT_OG_IMAGE = `${SITE_URL}/favicon.png`

export function absoluteUrl(path: string): string {
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

export function pageTitle(title: string): string {
  return `${title} | ${SITE_NAME}`
}

export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    description: DEFAULT_DESCRIPTION,
    sameAs: [],
  }
}

export function productJsonLd(product: Product) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `${product.name}${product.suite ? ' ' + product.suite : ''}`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: product.heroDescription,
    url: absoluteUrl(`/products/${product.slug}`),
    brand: { '@type': 'Organization', name: SITE_NAME },
    offers: product.pricingTiers.map((tier) => ({
      '@type': 'Offer',
      name: tier.name,
      description: tier.description,
    })),
  }
}

export function articleJsonLd(post: BlogPost) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    url: absoluteUrl(`/blog/${post.slug}`),
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: { '@type': 'Organization', name: SITE_NAME, logo: { '@type': 'ImageObject', url: DEFAULT_OG_IMAGE } },
  }
}

export function caseStudyJsonLd(study: CaseStudy) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${study.client}: ${study.summary}`,
    description: study.summary,
    url: absoluteUrl(`/case-studies/${study.slug}`),
    author: { '@type': 'Organization', name: SITE_NAME },
  }
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  }
}
